import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError } from '../config_and_helpers/logger';

// Multi-Component API Client
// Updated to work with the new MultiComponentViewSet backend endpoints

/**
 * Get stock items for a specific component
 * @param {number} componentId - The ID of the component product
 * @returns {Promise<Object>} - Response with component stock items
 */
export const getComponentStockItems = async componentId => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}multi_components/component_stock_items/`,
      {
        params: { component_id: componentId },
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'getComponentStockItems failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all components for a specific product
 * @param {number} productId - The ID of the multi-component product
 * @returns {Promise<Object>} - Response with product components
 */
export const getProductComponents = async productId => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}multi_components/product_components/`,
      {
        params: { product_id: productId },
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'getProductComponents failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get multi-component status for a specific product
 * @param {number} productId - The ID of the multi-component product
 * @returns {Promise<Object>} - Response with multi-component status
 */
export const getMultiComponentStatus = async productId => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}multi_component_products/${productId}/multi_component_status/`,
      {
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'getMultiComponentStatus failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Add a component to a multi-component product
 * @param {Object} componentData - Component data to add
 * @returns {Promise<Object>} - Response with added component info
 */
export const addComponent = async componentData => {
  try {
    const response = await axios.post(
      `${BACKEND_URL}multi_components/add_component/`,
      componentData,
      {
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'addComponent failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove a component from a multi-component product
 * @param {number} multiComponentId - The ID of the multi-component relationship
 * @param {number} componentId - The ID of the component to remove
 * @returns {Promise<Object>} - Response with removal status
 */
export const removeComponent = async (multiComponentId, componentId) => {
  try {
    const response = await axios.delete(
      `${BACKEND_URL}multi_components/${multiComponentId}/remove_component/`,
      {
        data: { component_id: componentId },
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'removeComponent failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a component's quantity or required status
 * @param {number} multiComponentId - The ID of the multi-component relationship
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} - Response with updated component info
 */
export const updateComponent = async (multiComponentId, updateData) => {
  try {
    const response = await axios.put(
      `${BACKEND_URL}multi_components/${multiComponentId}/update_component/`,
      updateData,
      {
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'updateComponent failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all available products that can be added as multi-components
 * @returns {Promise<Object>} - Response with available products
 */
export const getAvailableComponents = async () => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}multi_components/available_components/`,
      {
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'getAvailableComponents failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all products that support multi-components
 * @returns {Promise<Object>} - Response with multi-component products
 */
export const getMultiComponentProducts = async () => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}multi_component_products/available_products/`,
      {
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'getMultiComponentProducts failed', error);
    return { success: false, error: error.message };
  }
};

// Legacy functions for backward compatibility
// These will try the new endpoints first, then fall back to the old behavior

/**
 * Get component stock items for a specific master stock item
 * @param {number} masterStockItemId - The ID of the master stock item
 * @returns {Promise<Object>} - Response with component stock items
 */
export const getStockItemComponents = async masterStockItemId => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}multi_components/stock_item_components/`,
      {
        params: { master_stock_item_id: masterStockItemId },
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
        },
      }
    );
    return { success: true, data: response.data };
  } catch (error) {
    logError('multi_component_client', 'getStockItemComponents failed', error);
    return { success: false, error: error.message };
  }
};

/**
 * Legacy function - Update component status
 * @param {number} componentId - The ID of the component
 * @param {Object} statusData - Status data to update
 * @returns {Promise<Object>} - Response with update status
 */
export const updateComponentStatus = async (componentId, statusData) => {
  // This would need to be implemented based on your specific requirements
  // For now, return a success response
  return { success: true, data: { message: 'Component status updated' } };
};

/**
 * Legacy function - Delete component
 * @param {number} componentId - The ID of the component
 * @returns {Promise<Object>} - Response with deletion status
 */
export const deleteComponent = async componentId => {
  // This would need to be implemented based on your specific requirements
  // For now, return a success response
  return { success: true, data: { message: 'Component deleted' } };
};
